import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Avatar from '../../../base/avatar/components/Avatar';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconArrowUp, IconCheck, IconCloseLarge } from '../../../base/icons/svg';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { admitMultiple } from '../../../lobby/actions.web';
import { getKnockingParticipants, getLobbyEnabled } from '../../../lobby/functions';
import Drawer from '../../../toolbox/components/web/Drawer';
import JitsiPortal from '../../../toolbox/components/web/JitsiPortal';
import { showOverflowDrawer } from '../../../toolbox/functions.web';
import { useLobbyActions, useParticipantDrawer } from '../../hooks';
import LobbyParticipantItems from './LobbyParticipantItems';
import { changeDisplayLobby } from '../../actions.any';
const useStyles = makeStyles()(theme => {
    return {
        drawerActions: {
            listStyleType: 'none',
            margin: 0,
            padding: 0
        },
        drawerItem: {
            alignItems: 'center',
            color: theme.palette.text01,
            display: 'flex',
            padding: '12px 16px',
            ...withPixelLineHeight(theme.typography.bodyShortRegularLarge),
            '&:first-child': {
                marginTop: '15px'
            },
            '&:hover': {
                cursor: 'pointer',
                background: theme.palette.action02
            }
        },
        icon: {
            marginRight: 16
        },
        headingContainer: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            height: '48px'
        },
        heading: {
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            color: theme.palette.text02
        },
        lobby: {
            color: theme.palette.default00,
            fontSize: '16px',
            fontWeight: 700
        },
        link: {
            ...withPixelLineHeight(theme.typography.labelBold),
            color: theme.palette.redP01,
            cursor: 'pointer',
            padding: '7px 10px',
            borderRadius: '4px',
            backgroundColor: '#ee00330a',
            width: 'fit-content',
            alignSelf: 'flex-end',
            fontSize: '14px'
        }
    };
});
/**
 * Component used to display a list of participants waiting in the lobby.
 *
 * @returns {ReactNode}
 */
export default function LobbyParticipants() {
    const { isDisplayLobby } = useSelector((state) => state['features/participants-pane']);
    const state = useSelector((state) => StaticRange);
    const lobbyEnabled = useSelector(getLobbyEnabled);
    const participants = useSelector(getKnockingParticipants);
    const { t } = useTranslation();
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const admitAll = useCallback(() => {
        dispatch(admitMultiple(participants));
    }, [dispatch, participants]);
    const overflowDrawer = useSelector(showOverflowDrawer);
    const [drawerParticipant, closeDrawer, openDrawerForParticipant] = useParticipantDrawer();
    const [admit, reject] = useLobbyActions(drawerParticipant, closeDrawer);
    if (!lobbyEnabled || !participants.length) {
        return null;
    }
    const closeLobby = () => {
        dispatch(changeDisplayLobby(false));
    };
    const openLobby = () => {
        dispatch(changeDisplayLobby(true));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classes.headingContainer },
            (participants.length > 0) ?
                React.createElement("div", { className: classes.lobby }, t('participantsPane.headings.lobby', { count: participants.length }))
                : null,
            isDisplayLobby ?
                React.createElement(Icon, { onClick: () => closeLobby(), style: { cursor: 'pointer', marginRight: '8px' }, src: IconArrowUp, size: 18, color: '#44494D' })
                :
                    React.createElement(Icon, { onClick: () => openLobby(), style: { cursor: 'pointer', marginRight: '8px' }, src: IconArrowDown, size: 18, color: '#44494D' })),
        isDisplayLobby ?
            React.createElement("div", { style: { display: 'flex', flexDirection: 'column', marginBottom: '12px', borderRadius: '6px' } },
                participants.length > 1 &&
                    React.createElement("div", { className: classes.link, onClick: admitAll }, t('participantsPane.actions.admitAll')),
                React.createElement(LobbyParticipantItems, { openDrawerForParticipant: openDrawerForParticipant, overflowDrawer: overflowDrawer, participants: participants }))
            : null,
        React.createElement(JitsiPortal, null,
            React.createElement(Drawer, { isOpen: Boolean(drawerParticipant && overflowDrawer), onClose: closeDrawer },
                React.createElement("ul", { className: classes.drawerActions },
                    React.createElement("li", { className: classes.drawerItem },
                        React.createElement(Avatar, { className: classes.icon, participantId: drawerParticipant?.participantID, size: 20 }),
                        React.createElement("span", null, drawerParticipant?.displayName)),
                    React.createElement("li", { className: classes.drawerItem, onClick: reject },
                        React.createElement(Icon, { className: classes.icon, size: 20, src: IconCloseLarge }),
                        React.createElement("span", null, t('participantsPane.actions.reject'))),
                    React.createElement("li", { className: classes.drawerItem, onClick: admit },
                        React.createElement(Icon, { className: classes.icon, size: 20, src: IconCheck }),
                        React.createElement("span", null, t('participantsPane.actions.admit'))))))));
}
